import { useCallback, useEffect } from "react";

import { css } from "@compiled/react";

import { useSearchParameters } from "@/components/signup/hooks";
import {
    ufoEventRedirectToProduct,
    UfoCustomTimingKey,
    UfoCustomTimingWrapper,
    ProductKey
} from "@/components/signup/utils";
import { DwellTimeWrapper } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/components/dwell-time-wrapper";

import { useScreenManager } from "../../hooks/use-screen-manager";

const UserSegmentationView = () => {
    const { products, requestId, onDemandUrl, cloudId, n2n } =
        useSearchParameters();

    const { nextScreen } = useScreenManager();

    const listenForUserSegmentationPage = useCallback(
        (event: MessageEvent) => {
            let data;
            try {
                data = JSON.parse(event.data);
                if (data.source !== "user-segmentation-ui") {
                    data = null;
                }
            } catch (e) {
                data = null;
            }
            if (!data) {
                return;
            }
            if (!data.message) {
                return;
            }
            if (data.message === "finished") {
                nextScreen();
            }
        },
        [nextScreen]
    );

    // url is relative to WAC
    const iframeURL = window.IS_STORYBOOK
        ? "/iframe.html?args=&id=templates-wac-confirmation-page--user-segmentation-story-component&viewMode=story"
        : `/signup/account/setup?products=${products}&cloudId=${cloudId}&features=&requestId=${requestId}&ondemandurl=${onDemandUrl}&platform=web-platform${
              n2n ? "&n2n=true" : ""
          }`;

    useEffect(() => {
        const isProductJPD =
            products.length === 1 &&
            products.includes(ProductKey.JIRA_PRODUCT_DISCOVERY);
        const isProductJSM =
            products.length === 1 &&
            products.includes(ProductKey.JIRA_SERVICE_MANAGEMENT);
        if (isProductJPD || isProductJSM) {
            window.addEventListener(
                "message",
                listenForUserSegmentationPage,
                false
            );

            return () => {
                window.removeEventListener(
                    "message",
                    listenForUserSegmentationPage,
                    false
                );
            };
        }
        return;
    }, [listenForUserSegmentationPage, products]);

    return (
        <UfoCustomTimingWrapper
            ufoEvent={ufoEventRedirectToProduct}
            ufoCustomTimingKey={UfoCustomTimingKey.USER_SEGMENTATION_DWELL_TIME}
        >
            <DwellTimeWrapper
                eventData={{
                    eventName: "userSegmentationPageDwelled",
                    eventComponent: "userSegmentationPage",
                    event: "dwelled",
                    actionSubject: "userSegmentationPage",
                    source: "wac-confirmation-page"
                }}
                initialPollInterval={5000}
                idleTimeout={120000}
                nextPollIntervalCallback={(x) => x + 5000}
            >
                <div css={IframeContainer}>
                    <iframe
                        title={"User Segmentation"}
                        data-testid="user-seg-iframe"
                        frameBorder="0"
                        height="100%"
                        width="100%"
                        src={iframeURL}
                    />
                </div>
            </DwellTimeWrapper>
        </UfoCustomTimingWrapper>
    );
};

const IframeContainer = css({
    display: "flex",
    position: "fixed",
    height: "100vh",
    width: "100vw",
    zIndex: "99",
    insetBlockStart: "0",
    insetInlineStart: "0"
});

declare global {
    interface Window {
        IS_STORYBOOK?: boolean;
        PRODUCT_CATALOG_DELIVERY_KEY?: string;
        PRODUCT_CATALOG_SPACE?: string;
        PRODUCT_CATALOG_LOCATION_BASE_URL?: string;
    }
}

export default UserSegmentationView;
